// The device's copy of the jobs (IndexedDB jobData), shared by the office app and the
// techs app. Syncs only add and update, and a job is only downloaded while its
// Start_Date is inside the 2-year window, so a job deleted (or closed out of /data)
// while the device wasn't listening stayed on it for good. pruneDeletedJobs() asks
// the server for the ids in /data alone (a shallow read: keys, no records) and drops
// any local job that's gone, whatever its Start_Date.
import { getDB, fetchIdToken } from './utilities';
import { DB_URL } from './firebaseConfig';
import { pendingWrites } from './trackedDb';
import { isFirebaseConnected } from './initFirebase';

// Jobs made on this device that the server hasn't got yet: never pruned.
function unsentJobIds() {
  const ids = new Set();
  pendingWrites().forEach(w => {
    const m = /^\/?data\/([^/]+)/.exec(w.path || '');
    if (m) ids.add(m[1]);
  });
  try { // the techs app's own offline queue
    (JSON.parse(localStorage.getItem('techsOfflineQueue')) || []).forEach(i => {
      if (i.type === 'addOp' && i.payload?.newOp?.Job_ID) ids.add(i.payload.newOp.Job_ID);
    });
  } catch { /* storage blocked or unreadable */ }
  return ids;
}

/** Removes local jobs no longer in /data. Returns the ids removed ([] offline or on any error). */
export async function pruneDeletedJobs() {
  if (!isFirebaseConnected()) return [];
  try {
    const res = await fetch(`${DB_URL}/data.json?shallow=true&auth=${await fetchIdToken()}`);
    if (!res.ok) throw new Error(`shallow /data: ${res.status}`);
    const onServer = await res.json();
    if (!onServer || typeof onServer !== 'object') return []; // an empty answer is never trusted to wipe the cache
    const keep = unsentJobIds();
    const db = await getDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction('jobData', 'readwrite');
      const store = tx.objectStore('jobData');
      const removed = [];
      const req = store.getAllKeys();
      req.onsuccess = () => {
        req.result.forEach(id => {
          if (id === 'lastUpdated' || id in onServer || keep.has(id)) return;
          store.delete(id);
          removed.push(id);
        });
      };
      tx.oncomplete = () => resolve(removed);
      tx.onerror = e => reject(e.target?.error || e);
      tx.onabort = e => reject(e.target?.error || e);
    });
  } catch (err) {
    console.error('[jobCache] pruning deleted jobs failed', err);
    return [];
  }
}
